import { supabase } from "./supabaseClient";
import { assertRowsAffected } from "./db";
import { limitesDeHojeSaoPaulo } from "./format";
import type {
  AppUser,
  ContactAttemptComAutor,
  ImportItem,
  Lead,
  LeadComResponsavel,
  LeadStage,
  LeadStageEventComAutor,
  Motivo,
  MotivoCategoria,
} from "./types";

const LEAD_COM_RESPONSAVEL_SELECT =
  "*, responsavel:app_users!responsavel_id(nome), lote:import_batches!batch_id(nome)";

export interface FilaHojeResultado {
  atrasados: LeadComResponsavel[];
  hoje: LeadComResponsavel[];
}

/** Leads com proximo contato vencido ou para hoje, excluindo os que saíram de
 * circulação (arquivados ou marcados como "não contatar"). */
export async function fetchFilaHoje(): Promise<FilaHojeResultado> {
  const { fim } = limitesDeHojeSaoPaulo();

  const { data, error } = await supabase
    .from("leads")
    .select(LEAD_COM_RESPONSAVEL_SELECT)
    .is("arquivado_em", null)
    .eq("nao_contatar", false)
    .not("proximo_contato_em", "is", null)
    .lte("proximo_contato_em", fim.toISOString())
    .order("proximo_contato_em", { ascending: true });

  if (error) throw new Error(error.message);

  const leads = (data ?? []) as unknown as LeadComResponsavel[];
  const agora = Date.now();
  const atrasados = leads.filter(
    (l) => l.proximo_contato_em && new Date(l.proximo_contato_em).getTime() < agora,
  );
  const hoje = leads.filter(
    (l) => l.proximo_contato_em && new Date(l.proximo_contato_em).getTime() >= agora,
  );

  return { atrasados, hoje };
}

export async function fetchKanbanLeads(
  incluirArquivados: boolean,
): Promise<LeadComResponsavel[]> {
  let query = supabase
    .from("leads")
    .select(LEAD_COM_RESPONSAVEL_SELECT)
    .order("atualizado_em", { ascending: false });

  if (!incluirArquivados) {
    query = query.is("arquivado_em", null);
  }

  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as LeadComResponsavel[];
}

export async function fetchLeadById(id: string): Promise<LeadComResponsavel> {
  const { data, error } = await supabase
    .from("leads")
    .select(LEAD_COM_RESPONSAVEL_SELECT)
    .eq("id", id)
    .maybeSingle();

  if (error) throw new Error(error.message);
  if (!data) throw new Error("Lead não encontrado (ou sem acesso).");
  return data as unknown as LeadComResponsavel;
}

export interface LeadSearchFiltros {
  busca?: string;
  etapa?: LeadStage | "todas";
  responsavelId?: string | "todos";
  incluirArquivados?: boolean;
}

export async function searchLeads(
  filtros: LeadSearchFiltros,
): Promise<LeadComResponsavel[]> {
  let query = supabase.from("leads").select(LEAD_COM_RESPONSAVEL_SELECT);

  if (!filtros.incluirArquivados) {
    query = query.is("arquivado_em", null);
  }
  if (filtros.etapa && filtros.etapa !== "todas") {
    query = query.eq("etapa", filtros.etapa);
  }
  if (filtros.responsavelId && filtros.responsavelId !== "todos") {
    query = query.eq("responsavel_id", filtros.responsavelId);
  }
  if (filtros.busca && filtros.busca.trim() !== "") {
    const termo = filtros.busca.trim();
    query = query.or(
      `empresa.ilike.%${termo}%,cidade.ilike.%${termo}%,pessoa_contato.ilike.%${termo}%,telefone.ilike.%${termo}%`,
    );
  }

  query = query.order("criado_em", { ascending: false }).limit(500);

  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as LeadComResponsavel[];
}

/** Campos graváveis pelo cliente (ver GRANT UPDATE em
 * 20260902090600_rls_policies_grants.sql — colunas derivadas como
 * `nao_contatar`, `tentativas_total` etc. ficam de fora de propósito). */
export type LeadPatch = Partial<{
  empresa: string;
  ramo: string | null;
  cidade: string | null;
  uf: string | null;
  telefone: string | null;
  pessoa_contato: string | null;
  email: string | null;
  site: string | null;
  etapa: LeadStage;
  prioridade: Lead["prioridade"];
  responsavel_id: string | null;
  proximo_contato_em: string | null;
  motivo_id: number | null;
  motivo_outro: string | null;
  arquivado_em: string | null;
  arquivado_motivo: string | null;
  tags: string[];
  canal_permitido: Lead["canal_permitido"];
  // v1.1 (grant separado em 20260903100100_v11_reuniao_e_fechamento.sql)
  reuniao_em: string | null;
  nota_fechamento: string | null;
}>;

export async function updateLead(id: string, patch: LeadPatch): Promise<Lead> {
  const { data, error } = await supabase
    .from("leads")
    .update(patch)
    .eq("id", id)
    .select("*");

  if (error) throw new Error(error.message);
  const rows = assertRowsAffected(data, `atualizar lead ${id}`);
  return rows[0] as unknown as Lead;
}

export interface NovoLeadIndividual {
  empresa: string;
  ramo?: string | null;
  cidade?: string | null;
  uf?: string | null;
  telefone?: string | null;
  pessoa_contato?: string | null;
  email?: string | null;
  site?: string | null;
  prioridade?: Lead["prioridade"];
  responsavel_id?: string | null;
  tags?: string[];
  origem_fonte: string;
  origem_detalhe: string;
  origem_coletada_em: string;
}

export async function createLead(input: NovoLeadIndividual): Promise<Lead> {
  const { data, error } = await supabase
    .from("leads")
    .insert(input)
    .select("*")
    .single();

  if (error) throw new Error(error.message);
  return data as unknown as Lead;
}

export async function fetchImportItems(batchId: string): Promise<ImportItem[]> {
  const { data, error } = await supabase
    .from("import_items")
    .select("*")
    .eq("batch_id", batchId)
    .order("linha_num", { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as ImportItem[];
}

export async function fetchMotivos(categoria?: MotivoCategoria): Promise<Motivo[]> {
  let query = supabase.from("motivos").select("*").eq("ativo", true).order("ordem");
  if (categoria) query = query.eq("categoria", categoria);
  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as Motivo[];
}

/** Supressão ativa (nao revogada) originada por este lead — usada so para
 * oferecer o botao "Revogar nao contatar" na ficha. Uma supressao tambem
 * pode ter sido criada por OUTRO lead com a mesma identidade normalizada
 * (telefone ou empresa+cidade); nesse caso nao ha o que revogar aqui, o
 * usuario precisa ir na lista de supressao (fora do escopo do v1). */
export async function fetchSupressaoAtivaPorLead(
  leadId: string,
): Promise<{ id: string } | null> {
  const { data, error } = await supabase
    .from("nao_contatar")
    .select("id")
    .eq("origem_lead_id", leadId)
    .is("revogado_em", null)
    .order("marcado_em", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data as { id: string } | null;
}

export async function fetchAppUsers(): Promise<AppUser[]> {
  const { data, error } = await supabase
    .from("app_users")
    .select("*")
    .eq("ativo", true)
    .order("nome");
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as AppUser[];
}

export async function fetchContactAttempts(
  leadId: string,
): Promise<ContactAttemptComAutor[]> {
  const { data, error } = await supabase
    .from("contact_attempts")
    .select("*, autor:app_users!autor_id(nome)")
    .eq("lead_id", leadId)
    .order("ocorreu_em", { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as ContactAttemptComAutor[];
}

/** "Dias na etapa" nao e uma coluna do schema — deriva do evento mais recente
 * de lead_stage_events por lead (que, por construcao do trigger, sempre tem
 * `para` igual a etapa atual do lead). Uma consulta para todos os leads
 * visiveis, em vez de N consultas por card. */
export async function fetchDataEntradaEtapaPorLead(
  leadIds: string[],
): Promise<Map<string, string>> {
  if (leadIds.length === 0) return new Map();

  const { data, error } = await supabase
    .from("lead_stage_events")
    .select("lead_id, ocorreu_em")
    .in("lead_id", leadIds)
    .order("ocorreu_em", { ascending: false });

  if (error) throw new Error(error.message);

  const mapa = new Map<string, string>();
  for (const row of (data ?? []) as { lead_id: string; ocorreu_em: string }[]) {
    if (!mapa.has(row.lead_id)) mapa.set(row.lead_id, row.ocorreu_em);
  }
  return mapa;
}

export async function fetchLeadStageEvents(
  leadId: string,
): Promise<LeadStageEventComAutor[]> {
  const { data, error } = await supabase
    .from("lead_stage_events")
    .select("*, autor:app_users!autor_id(nome), motivo:motivos!motivo_id(rotulo)")
    .eq("lead_id", leadId)
    .order("ocorreu_em", { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as LeadStageEventComAutor[];
}

/** Anula uma tentativa (correção de engano) — nunca DELETE, ver comentário da
 * tabela contact_attempts. `anulado_motivo` é opcional na constraint, mas
 * pedimos sempre para manter a trilha auditável legível. */
export async function anularContactAttempt(
  attemptId: string,
  autorId: string,
  motivo: string,
): Promise<void> {
  const { data, error } = await supabase
    .from("contact_attempts")
    .update({
      anulado_em: new Date().toISOString(),
      anulado_por: autorId,
      anulado_motivo: motivo,
    })
    .eq("id", attemptId)
    .is("anulado_em", null)
    .select("id");

  if (error) throw new Error(error.message);
  assertRowsAffected(data, `anular tentativa ${attemptId}`);
}
